<template>
  <div class="camera-container">
    <div class="video-wrapper">
      <!-- 红圈定位在视频上面 -->
      <div v-show="!faceDetected" class="face-detection-circle"></div>

      <div class="video-circle">
        <video ref="video" autoplay muted playsinline width="100%" height="100%" v-show="videoStarted"></video>

        <!-- 检测不到摄像头时显示重试提示 -->
        <div v-show="!videoStarted" class="retry-circle" @click="retryVideo">
          <span class="retry-text">点击重试</span>
        </div>

        <canvas ref="canvas" width="100%" height="100%" style="position: absolute; top: 0; left: 0;"></canvas>

        <!-- 当上传中时，显示灰色背景和加载提示 -->
        <div class="uploading-overlay" v-show="isloading">
          <div class="loading-spinner"></div>
        </div>
      </div>
    </div>

    <!-- 错误信息提示 -->
    <div v-show="errorMessage" class="text-below-circle">
      <p>{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from "vue";
import * as faceapi from "face-api.js";
export default {
  name: "FaceView", // 组件名称
  setup() {
    const mediaStream = ref(null); // 用来保存视频流
    const videoStarted = ref(true); // 标记视频流是否已成功启动
    const lastNosePosition = ref(null); //旧鼻子位置
    const errorMessage = ref(null); //提示
    const isloading = ref(false); //是否转圈
    const faceDetected = ref(true);  // 有没有脸

    const bgColors = ["255, 0, 0", "0, 0, 255", "0, 255, 0"]; // 红色、蓝色、绿色
    const bgColorIndex = ref(0); // 当前背景颜色索引
    const isChangingColor = ref(false); // 标志是否正在变色
    const changeCount = ref(0);  // 记录变色次数
    const colorChangeDelay = 1000; // 每秒变化一次颜色
    // const changeCountLimit = 3; // 最大变色次数
    const capturedImage = ref(null); // 临时图片


    // const mediaStream = ref(null); // 用来保存视频流
    // const videoStarted = ref(true); // 标记视频流是否已成功启动
    // const lastNosePosition = ref(null);//旧鼻子位置
    // const errorMessage = ref(null);//提示

    // const bgColors = ["255, 0, 0", "0, 0, 255", "0, 255, 0"];  // 红色、蓝色、绿色
    // const bgColorIndex = ref(0);  // 当前背景颜色索引
    // const isChangingColor = ref(false);  // 标志是否正在变色
    // 定义颜色变化的间隔时间
    // const colorChangeDelay = 1000;  // 每隔 1 秒改变一次颜色
    // const changeCount = ref(0);  // 记录变色次数
    // 响应式数据
    // const faceDetected = ref(true); // 有没有脸
    // const isUploading = ref(false); //是否上传中
    // const isloading = ref(false); //是否上传中

    onMounted(() => {
      document.body.style.transition = 'background-color 0.8s ease';
      loadModels().then(() => {
        startVideoStream();
      });
    });
    // 在销毁组件时清理视频流
    onBeforeUnmount(() => {
      stopVideoStream();
    });
    // 加载 face-api.js 模型
    const loadModels = async () => {
      try {
        await faceapi.nets.ssdMobilenetv1.loadFromUri("/models");
        await faceapi.nets.faceLandmark68Net.loadFromUri("/models");
        await faceapi.nets.faceExpressionNet.loadFromUri("/models");
        console.log("所有模型加载完成");
      } catch (error) {
        console.error("模型加载失败", error);
      }
    };
    // 启动视频流
    const startVideoStream = async () => {
      const videoElement = document.querySelector("video");
      if (videoElement) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: {} });
          mediaStream.value = stream;
          videoElement.srcObject = stream;
          videoElement.onloadedmetadata = () => {
            videoElement.play();
            videoStarted.value = true;
            detectFace();
          };
        } catch (error) {
          console.error("视频流启动失败", error);
          errorMessage.value = "无法启动摄像头，请重试。";
          videoStarted.value = false;
        }
      }
    };
    // 重试获取摄像头
    const retryVideo = () => {
      mediaStream.value = null;
      startVideoStream();
    };
    // 停止视频流
    const stopVideoStream = () => {
      if (mediaStream.value) {
        const tracks = mediaStream.value.getTracks();
        tracks.forEach((track) => track.stop());
      }
      videoStarted.value = false;
    };

    // 检测人脸
    const detectFace = () => {
      const videoElement = document.querySelector("video");
      const canvasElement = document.querySelector("canvas");

      const displaySize = { width: videoElement.width, height: videoElement.height };
      faceapi.matchDimensions(videoElement, displaySize);

      setInterval(async () => {
        try {
          const detections = await faceapi
            .detectAllFaces(videoElement, new faceapi.SsdMobilenetv1Options())
            .withFaceLandmarks()
            .withFaceExpressions();

          if (detections.length > 0) {
            faceDetected.value = true;

            const box = detections[0].detection.box;
            const faceWidth = box.width;
            const faceWidthPercentage = (faceWidth / videoElement.videoWidth) * 100;

            if (faceWidthPercentage < 40) {
              errorMessage.value = '请靠近一点';
              stopColorChange();
              return;
            } else {
              errorMessage.value = '请不要动';
              const nose = detections[0].landmarks.getNose();
              if (!lastNosePosition.value) {
                lastNosePosition.value = nose;
                return;
              }

              const deltaNose = Math.abs(nose[0].x - lastNosePosition.value[0].x);
              lastNosePosition.value = nose;

              if (deltaNose > 5) {
                errorMessage.value = '请不要动';
                stopColorChange();
                return;
              }

              if (deltaNose < 4) {
                errorMessage.value = '请保持姿势不变';
                startColorChange();
              }
            }
          } else {
            errorMessage.value = '没有检测到人脸';
            faceDetected.value = false;
            stopColorChange();
          }

          const context = canvasElement.getContext("2d");
          context.clearRect(0, 0, canvasElement.width, canvasElement.height);
        } catch (error) {
          console.error("面部检测失败", error);
          errorMessage.value = '请移动人脸到框内';
          stopColorChange();
        }
      }, 100);
    };

    // 递归变色
    const changeBackgroundColor = () => {
      if (!isChangingColor.value || changeCount.value >= 3) return;

      if (changeCount.value == 2 && !capturedImage.value) {
        captureImage()
      }

      changeCount.value++;

      bgColorIndex.value = (bgColorIndex.value + 1) % bgColors.length;
      const colorWithOpacity = `rgba(${bgColors[bgColorIndex.value]}, 0.7)`;
      document.body.style.backgroundColor = colorWithOpacity;
      console.log(colorWithOpacity);

      setTimeout(changeBackgroundColor, colorChangeDelay);
    };

    // 开始颜色变换
    const startColorChange = () => {
      // console.log("开始变色");

      if (isChangingColor.value) {
        // console.log("变色已在进行中，跳过");
        return;
      }
      isloading.value = true;
      isChangingColor.value = true;
      changeCount.value = 0;
      changeBackgroundColor();
    };

    // 停止颜色变换
    const stopColorChange = () => {
      if (isChangingColor.value) {
        console.log("停止变色");
        isloading.value = false;
        isChangingColor.value = false;
        document.body.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
      }
    };

    // 捕获图像
    const captureImage = () => {
      const canvasElement = document.querySelector("canvas");
      const videoElement = document.querySelector("video");

      canvasElement.width = videoElement.videoWidth;
      canvasElement.height = videoElement.videoHeight;

      const context = canvasElement.getContext("2d");
      context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);

      capturedImage.value = canvasElement.toDataURL("image/jpeg", 1);
      console.log("拍照成功");
      uploadFile()
    };

    // 上传图片
    const uploadFile = async () => {
      if (!capturedImage.value) {
        errorMessage.value = "请先捕获图像";
        return;
      }

      try {
        const formData = new FormData();

        // 将Base64数据转换为Blob
        const base64Data = capturedImage.value.split(",")[1]; // 去掉 "data:image/jpeg;base64," 部分
        const byteCharacters = atob(base64Data); // 解码Base64
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512);
          const byteNumbers = new Array(slice.length);

          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
          }

          const byteArray = new Uint8Array(byteNumbers);
          byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: "image/jpeg" }); // 创建Blob对象
        formData.append("file", blob, "captured-image.jpg");  // 将Blob对象添加到FormData
        formData.append("dir", "faceswap/232323");
        // 上传到服务器
        const response = await fetch("https://file.uslaoma-588.workers.dev/upload", {
          method: "POST",
          body: formData,
          headers: {
            "token": "your-secure-password"  // 添加自定义头部
          }
        });

        if (response.ok) {
          const result = await response.json();
          console.log("上传成功", result);
          errorMessage.value = null;  // 清空错误信息
        } else {
          throw new Error("上传失败，请稍后重试");
        }
      } catch (error) {
        errorMessage.value = error.message;
        console.error("上传错误", error);
      }
      stopVideoStream()
    };
    return {
      videoStarted,
      errorMessage,


      faceDetected,

      isloading,
      retryVideo
    };
  },

}

</script>

<style scoped>
/* body {
  transition: background-color 0.5s ease;
} */
.camera-container {
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
}

.video-wrapper {
  position: relative;
  width: 200px;
  /* 确保视频和红圈大小一致 */
  height: 200px;
  /* 确保视频和红圈大小一致 */
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 150px;
}

.video-circle {
  position: absolute;
  /* 绝对定位 */
  top: 0;
  left: 0;
  width: 100%;
  /* 确保占满整个容器 */
  height: 100%;
  /* 确保占满整个容器 */
  border-radius: 50%;
  overflow: hidden;
}

video {
  object-fit: cover;
  /* 确保视频按比例填充圆形 */
  width: 100%;
  height: 100%;
}

.face-detection-circle {
  position: absolute;
  /* 绝对定位 */
  width: 100%;
  /* 保证大小与视频容器一致 */
  height: 100%;
  /* 保证大小与视频容器一致 */
  border: 5px solid red;
  /* 红色边框 */
  border-radius: 50%;
  /* 圆形 */
  pointer-events: none;
  /* 确保不干扰视频播放 */
}

.text-below-circle {
  position: absolute;
  /* 绝对定位 */
  margin-top: 450px;
  /* bottom: 180px; 将文本显示在视频下方 */
  font-size: 18px;
  text-align: center;
  color: red;
}

/* 添加圆圈背景和加载文本 */
.uploading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(128, 128, 128, 0.6);
  /* 灰色半透明背景 */
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
}

/* 创建旋转的加载圈 */
.loading-spinner {
  border: 4px solid #f3f3f3;
  /* 灰色背景 */
  border-top: 4px solid transparent;
  /* 蓝色圆环 */
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1.5s linear infinite;
  /* 旋转动画 */
}

/* 定义旋转动画 */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* 在未检测到视频流时，显示一个点击重试的圆圈 */
.retry-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-size: 16px;
  color: white;
  font-weight: bold;
}

.retry-text {
  text-align: center;
}
</style>